/**
 * GraphQL client for ai-service ("sua IA"). Same fetch pattern as the other
 * service modules: token param, env-driven URL, throw on GraphQL errors.
 * The AI runs 100% server-side — this only sends the user's message + JWT.
 * Accept-Language drives the assistant's default answer language.
 */
import i18n from '../../i18n';

const API_URL = process.env.REACT_APP_AI_SERVICE_BASE_URL || '';

export interface AiProposalField {
    label: string;
    value: string;
}

/** One action proposal (propose → confirm → execute). Rendered as a card built
 *  from these SERVER-side values — never from the model's text. */
export interface AiProposal {
    id: string;
    action: string;
    title: string;
    details: AiProposalField[];
    expiresAt: string | null;
}

export interface AiChatResult {
    conversationId: string;
    reply: string;
    proposals: AiProposal[];
}

export interface AiProposalResult {
    ok: boolean;
    message: string;
}

export async function aiChat(
    businessId: number,
    message: string,
    conversationId: string | null,
    token: string,
    /** Label of the admin screen currently open — lets "explica essa tela" work. */
    screen?: string | null,
): Promise<AiChatResult> {
    if (!API_URL) throw new Error('AI service URL not configured');
    const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept-Language': i18n.language || 'pt',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            query: `
                mutation aiChat($businessId: ID!, $message: String!, $conversationId: ID, $screen: String) {
                    aiChat(businessId: $businessId, message: $message, conversationId: $conversationId, screen: $screen) {
                        conversationId
                        reply
                        proposals { id action title details { label value } expiresAt }
                    }
                }`,
            variables: { businessId, message, conversationId, screen: screen ?? null },
        }),
    });
    if (!response.ok) throw new Error(`ai-service ${response.status}`);
    const json = await response.json();
    if (json.errors?.length) {
        throw new Error(json.errors[0]?.message || 'AI error');
    }
    return json.data.aiChat as AiChatResult;
}

export interface AiConversationSummary {
    id: string;
    title: string | null;
    updatedOn: string | null;
}

export interface AiMessage {
    role: 'USER' | 'ASSISTANT';
    content: string;
    createdOn: string | null;
}

/** The caller's own conversations in this business (for the "Recentes" rail). */
export async function aiConversations(businessId: number, token: string): Promise<AiConversationSummary[]> {
    const json = await post(
        `query($businessId: ID!) {
            aiConversations(businessId: $businessId, limit: 20) { id title updatedOn }
        }`,
        { businessId }, token);
    return (json.data?.aiConversations ?? []) as AiConversationSummary[];
}

/** Messages of one of the caller's conversations. */
export async function aiMessages(businessId: number, conversationId: string, token: string): Promise<AiMessage[]> {
    const json = await post(
        `query($businessId: ID!, $conversationId: ID!) {
            aiMessages(businessId: $businessId, conversationId: $conversationId) { role content createdOn }
        }`,
        { businessId, conversationId }, token);
    return (json.data?.aiMessages ?? []) as AiMessage[];
}

/** Still-pending, unexpired proposals of a conversation — re-renders the
 *  confirmation cards when an old conversation is reopened. Expired proposals
 *  never come back (filtered server-side). */
export async function aiPendingProposals(
    businessId: number,
    conversationId: string,
    token: string,
): Promise<AiProposal[]> {
    const json = await post(
        `query($businessId: ID!, $conversationId: ID!) {
            aiPendingProposals(businessId: $businessId, conversationId: $conversationId) {
                id action title details { label value } expiresAt
            }
        }`,
        { businessId, conversationId }, token);
    return (json.data?.aiPendingProposals ?? []) as AiProposal[];
}

/** Executes a pending proposal. The backend re-validates ownership, expiry and
 *  the EDIT permission before running anything. */
export async function aiConfirmProposal(
    businessId: number,
    proposalId: string,
    token: string,
): Promise<AiProposalResult> {
    const json = await post(
        `mutation($businessId: ID!, $proposalId: ID!) {
            aiConfirmProposal(businessId: $businessId, proposalId: $proposalId) { ok message }
        }`,
        { businessId, proposalId }, token);
    return json.data.aiConfirmProposal as AiProposalResult;
}

/** Discards a pending proposal (nothing is executed). */
export async function aiRejectProposal(
    businessId: number,
    proposalId: string,
    token: string,
): Promise<boolean> {
    const json = await post(
        `mutation($businessId: ID!, $proposalId: ID!) {
            aiRejectProposal(businessId: $businessId, proposalId: $proposalId)
        }`,
        { businessId, proposalId }, token);
    return Boolean(json.data?.aiRejectProposal);
}

async function post(query: string, variables: Record<string, unknown>, token: string) {
    if (!API_URL) throw new Error('AI service URL not configured');
    const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept-Language': i18n.language || 'pt',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ query, variables }),
    });
    if (!response.ok) throw new Error(`ai-service ${response.status}`);
    const json = await response.json();
    if (json.errors?.length) throw new Error(json.errors[0]?.message || 'AI error');
    return json;
}
